import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { addPackageItem, updatePackageItem, updateProduct } from '@/lib/api';
import type { GiftPackageItem, GiftProduct, PackageSection } from '@/lib/types';

interface Props {
  open: boolean;
  onClose: () => void;
  packageId: string;
  section: PackageSection;
  onAdded: () => void;
  /**
   * Wanneer gezet: bewerk-modus voor een bestaand (handmatig) pakket-item i.p.v.
   * een nieuw item toevoegen. Het gekoppelde gift_product + item-aantal worden
   * aangepast in plaats van aangemaakt.
   */
  editItem?: GiftPackageItem | null;
}

const OVERIG_SUPPLIER_ID = 'f3842105-a90f-4b65-988f-13738f55c56f';

const inputCls =
  'w-full rounded-md border border-bmade-200 bg-white px-3 py-2 text-sm text-bmade-950 outline-none focus:border-accent-500 dark:border-bmade-700 dark:bg-bmade-950/60 dark:text-white';

const labelCls =
  'mb-1 block text-xs uppercase tracking-wider text-bmade-600 dark:text-bmade-400';

function productTypeFor(section: PackageSection): GiftProduct['product_type'] {
  if (section === 'verpakking') return 'non_food';
  if (section === 'drank') return 'alcohol';
  if (section === 'food') return 'food';
  return 'non_food';
}

export function CustomItemModal({ open, onClose, packageId, section, onAdded, editItem }: Props) {
  const [name, setName] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [price, setPrice] = useState('');
  const [vpe, setVpe] = useState('1');
  const [qtyPerPackage, setQtyPerPackage] = useState('1');
  const [btw, setBtw] = useState<'low' | 'high'>(section === 'food' ? 'low' : 'high');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const editProduct = editItem?.gift_product ?? null;

  // Bij openen: velden vullen uit het te bewerken product (edit-modus) of legen
  // voor een nieuw item (create-modus). De modal blijft gemount, dus we syncen
  // de state via een effect i.p.v. via state-initializers.
  useEffect(() => {
    if (!open) return;
    setError(null);
    if (editProduct) {
      setName(editProduct.name);
      setSupplierName(editProduct.brand ?? '');
      setPrice(editProduct.unit_purchase_price != null ? String(editProduct.unit_purchase_price) : '');
      setVpe(String(editProduct.vpe ?? 1));
      setQtyPerPackage(String(editItem?.quantity_per_package ?? 1));
      setBtw(editProduct.btw_category ?? (section === 'food' ? 'low' : 'high'));
    } else {
      setName('');
      setSupplierName('');
      setPrice('');
      setVpe('1');
      setQtyPerPackage('1');
      setBtw(section === 'food' ? 'low' : 'high');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editItem]);

  if (!open) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      // EDIT-modus: bestaand (handmatig) product + item-aantal bijwerken.
      if (editItem && editProduct) {
        await updateProduct(editProduct.id, {
          name: name.trim(),
          brand: supplierName.trim() || null,
          unit_purchase_price: parseFloat(price) || 0,
          vpe: parseInt(vpe) || 1,
          btw_category: btw,
        });
        const itemPatch: Partial<GiftPackageItem> = {};
        const nextQty = parseInt(qtyPerPackage) || 1;
        if (nextQty !== editItem.quantity_per_package) itemPatch.quantity_per_package = nextQty;
        // De modal-prijs is leidend: hef een eventuele per-item override op zodat
        // de nieuwe productprijs zichtbaar wordt.
        if (editItem.unit_purchase_price_override != null) {
          itemPatch.unit_purchase_price_override = null;
        }
        if (Object.keys(itemPatch).length > 0) await updatePackageItem(editItem.id, itemPatch);
        onAdded();
        onClose();
        return;
      }

      const fullName = supplierName ? `${supplierName} — ${name}` : name;
      const { data: product, error: pErr } = await supabase
        .from('gift_products')
        .insert({
          supplier_id: OVERIG_SUPPLIER_ID,
          name: fullName,
          brand: supplierName || null,
          product_type: productTypeFor(section),
          is_packaging: section === 'verpakking',
          btw_category: btw,
          unit_purchase_price: parseFloat(price) || 0,
          vpe: parseInt(vpe) || 1,
          active: true,
          source_file: 'handmatig',
          source_year: new Date().getFullYear(),
        })
        .select()
        .single();
      if (pErr) throw pErr;

      await addPackageItem({
        package_id: packageId,
        gift_product_id: product.id,
        quantity_per_package: parseInt(qtyPerPackage) || 1,
        selling_price_per_item: (product.unit_purchase_price ?? 0) / 0.7,
      });

      onAdded();
      onClose();
      setName('');
      setSupplierName('');
      setPrice('');
      setVpe('1');
      setQtyPerPackage('1');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Toevoegen mislukt');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-bmade-950/60 px-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl border border-bmade-200 bg-white shadow-xl dark:border-bmade-800 dark:bg-bmade-900">
        <div className="flex items-center justify-between border-b border-bmade-200 px-5 py-3 dark:border-bmade-800">
          <h2 className="text-base font-semibold text-bmade-950 dark:text-white">
            {editItem ? 'Item bewerken' : 'Handmatig item toevoegen'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-bmade-500 hover:bg-bmade-100 hover:text-bmade-900 dark:text-bmade-400 dark:hover:bg-bmade-800 dark:hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 px-5 py-4">
          <div>
            <label className={labelCls}>Productnaam</label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="bv. MOB Bamboe pen"
              className={inputCls}
              autoFocus
            />
          </div>
          <div>
            <label className={labelCls}>Leverancier (vrije tekst)</label>
            <input
              type="text"
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
              placeholder="bv. MOB"
              className={inputCls}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Inkoopprijs / stuk</label>
              <input
                type="number"
                required
                min={0}
                step={0.01}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Colli (VPE)</label>
              <input
                type="number"
                required
                min={1}
                value={vpe}
                onChange={(e) => setVpe(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Aantal per pakket</label>
              <input
                type="number"
                required
                min={1}
                value={qtyPerPackage}
                onChange={(e) => setQtyPerPackage(e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>BTW</label>
              <select value={btw} onChange={(e) => setBtw(e.target.value as 'low' | 'high')} className={inputCls}>
                <option value="low">9% (food / non-alcohol drink)</option>
                <option value="high">21% (gadget / alcohol / verpakking)</option>
              </select>
            </div>
          </div>

          {error && (
            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-bmade-200 px-3 py-2 text-sm text-bmade-700 hover:border-bmade-400 dark:border-bmade-700 dark:text-bmade-200"
            >
              Annuleren
            </button>
            <button
              type="submit"
              disabled={busy}
              className="rounded-md bg-accent-500 px-3 py-2 text-sm font-medium text-white hover:bg-accent-600 disabled:opacity-50"
            >
              {busy ? 'Bezig...' : editItem ? 'Opslaan' : 'Toevoegen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
